/**
 * 云盘链接
 * - 文件夹: /cloudStorage/{code}（前端 encode/decode，不在 URL 暴露明文 id）
 * - 文件:   /yun/{code}
 */

const CLOUD_STORAGE_ID_PREFIX = 'kbcs:'

function kbDomain () {
  const domain = process.env.VUE_APP_DOMAIN || 'https://kb.nihaocafe.com/'
  return domain.endsWith('/') ? domain.slice(0, -1) : domain
}

function toUrlSafeBase64 (value) {
  return btoa(value)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

function fromUrlSafeBase64 (segment) {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
  return atob(padded)
}

/** 文件夹 id → 路由 segment（Copy Link / 内部跳转） */
export function encodeCloudStorageFolderId (id) {
  if (id == null || id === '') return 'All'
  return toUrlSafeBase64(`${CLOUD_STORAGE_ID_PREFIX}${id}`)
}

/** 路由 segment → 文件夹 id；兼容旧链纯数字 segment */
export function decodeCloudStorageFolderSegment (segment) {
  if (!segment || segment === 'All') return null
  if (/^\d+$/.test(segment)) return Number(segment)
  try {
    const decoded = fromUrlSafeBase64(segment)
    if (!decoded.startsWith(CLOUD_STORAGE_ID_PREFIX)) return null
    const id = Number(decoded.slice(CLOUD_STORAGE_ID_PREFIX.length))
    return Number.isFinite(id) ? id : null
  } catch {
    return null
  }
}

function folderSegment (id) {
  return encodeCloudStorageFolderId(id)
}

export function buildYunShareableLink (code) {
  return code ? `${kbDomain()}/yun/${code}` : ''
}

export function buildCloudStorageFolderLink (id) {
  return `${kbDomain()}/cloudStorage/${folderSegment(id)}`
}

export function cloudStorageFolderRoute (id) {
  return {
    name: 'CloudStorage',
    params: { folder: folderSegment(id) }
  }
}

/**
 * 解析文件夹路由
 * @returns {{ id: number|null, needsNormalize: boolean }}
 */
export function parseCloudStorageFolderRoute (route) {
  const queryId = route.query?.id ? Number(route.query.id) : null
  const segment = route.params?.folder || ''
  const pathId = decodeCloudStorageFolderSegment(segment)
  const id = queryId || pathId || null

  if (segment === 'All' && id == null) {
    return { id: null, needsNormalize: false }
  }

  const canonicalSegment = folderSegment(id)
  const isCanonical =
    id != null && segment === canonicalSegment && !route.query?.id
  return { id, needsNormalize: id != null && !isCanonical }
}

/** 从 OSS URL 解析 ossPath 与文件名（先去掉 ?query，避免 Signature 中 %2F 干扰） */
export function parseOssStorageUrl (path) {
  if (!path) return { ossPath: '', fileName: '' }
  const pathOnly = String(path).split('?')[0]
  const segments = pathOnly.split('/').filter(Boolean)
  // https: / / host / 2026-04 / 86980 / filename.jpg → 前 3 段为协议与域名
  const encodedOssPath = segments.slice(3).join('/')
  let ossPath = encodedOssPath
  try {
    ossPath = decodeURIComponent(encodedOssPath)
  } catch {
    ossPath = encodedOssPath
  }
  const fileName = ossPath.split('/').filter(Boolean).pop() || ''
  return { ossPath, fileName }
}

/** @deprecated 请用 parseOssStorageUrl */
export function filenameFromStoragePath (path) {
  return parseOssStorageUrl(path).fileName
}

/** 预览用文件名：优先带后缀的名称，避免无后缀 folderName 盖住 path 中的 .jpg */
export function resolvePreviewFileName (meta, fileParam) {
  if (fileParam) return fileParam
  const hasSuffix = (name) => {
    if (!name) return false
    const dot = name.lastIndexOf('.')
    return dot > 0 && dot < name.length - 1
  }
  const candidates = [
    meta.folderName,
    meta.fileName,
    meta.pathName,
    meta.ossPath?.split('/').filter(Boolean).pop()
  ]
  for (const name of candidates) {
    if (hasSuffix(name)) return name
  }
  return meta.folderName || meta.fileName || meta.pathName || candidates[3] || ''
}
